import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { applyPanel } from "../ui/UiPanel";
const CODE_LENGTH = 6;
export default function CodeEntryScreen({ onCodeValid, onBack }) {
    const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
    const [error, setError] = useState("");
    const [checking, setChecking] = useState(false);
    const boxRef = useRef(null);
    const inputRefs = useRef([]);
    useEffect(() => {
        if (boxRef.current)
            applyPanel(boxRef.current, "beigeLight");
    }, []);
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);
    async function checkCode(code) {
        setError("");
        setChecking(true);
        try {
            const res = await fetch(`/api/invite/user-code/${encodeURIComponent(code)}`);
            if (!res.ok) {
                const err = (await res.json());
                setError(err.error ?? "Ungültiger Code");
                return;
            }
            onCodeValid(code);
        }
        catch {
            setError("Code konnte nicht geprüft werden");
        }
        finally {
            setChecking(false);
        }
    }
    function setDigitAt(index, value) {
        const next = [...digits];
        next[index] = value;
        setDigits(next);
        return next;
    }
    function handleChange(index, rawValue) {
        setError("");
        const value = rawValue
            .replace(/[^a-zA-Z0-9]/g, "")
            .toUpperCase()
            .slice(-1);
        const next = setDigitAt(index, value);
        if (value && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
        if (value && next.every((d) => d !== "")) {
            void checkCode(next.join(""));
        }
    }
    function handleKeyDown(index, e) {
        if (e.key === "Backspace" && !digits[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
            setDigitAt(index - 1, "");
        }
        else if (e.key === "ArrowLeft" && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    }
    function handlePaste(e) {
        const pasted = e.clipboardData
            .getData("text")
            .replace(/[^a-zA-Z0-9]/g, "")
            .toUpperCase();
        if (!pasted)
            return;
        e.preventDefault();
        setError("");
        const next = Array(CODE_LENGTH).fill("");
        for (let i = 0; i < CODE_LENGTH; i++) {
            next[i] = pasted[i] ?? "";
        }
        setDigits(next);
        const lastFilled = Math.min(pasted.length, CODE_LENGTH) - 1;
        if (lastFilled >= 0)
            inputRefs.current[Math.min(lastFilled, CODE_LENGTH - 1)]?.focus();
        if (pasted.length >= CODE_LENGTH) {
            void checkCode(next.join(""));
        }
    }
    const complete = digits.every((d) => d !== "");
    return (_jsxs("div", { className: "min-h-screen bg-lime-700 flex items-center justify-center font-sans", children: [_jsx("img", { src: "/assets/images/gpt-stolpen.webp", alt: "Background image: Stolpen", className: "absolute top-0 left-0 w-full h-full object-cover z-0 blur-sm" }), _jsxs("div", { ref: boxRef, className: "relative z-10 p-8 w-[360px] flex flex-col gap-4 shadow-sm", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-slate-900 m-0", children: "Dein GO! City" }), _jsx("p", { className: "text-sm text-slate-600 mt-1", children: "Gib deinen Einladungscode ein." })] }), _jsx("div", { className: "flex justify-between gap-2", children: digits.map((digit, i) => (_jsx("input", { ref: (el) => {
                                inputRefs.current[i] = el;
                            }, type: "text", inputMode: "text", autoCapitalize: "characters", autoComplete: "off", maxLength: 1, value: digit, disabled: checking, onChange: (e) => handleChange(i, e.target.value), onKeyDown: (e) => handleKeyDown(i, e), onPaste: handlePaste, className: "w-full aspect-square min-w-0 text-center text-lg font-semibold uppercase border border-[#7d6650] rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50" }, i))) }), _jsx("p", { className: "text-red-700 text-xs min-h-[1rem] m-0", children: error }), _jsx("button", { onClick: () => void checkCode(digits.join("")), disabled: !complete || checking, className: "w-full py-2 bg-lime-400 border-transparent border hover:bg-lime-300 hover:border-lime-400 text-black font-medium rounded-lg cursor-pointer text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed", children: "Weiter" }), _jsx("div", { className: "text-center", children: _jsx("button", { type: "button", onClick: onBack, className: "text-xs text-slate-600 hover:text-slate-800 underline cursor-pointer bg-transparent border-0 p-0", children: "Zur\u00FCck zur Anmeldung" }) })] })] }));
}
